/**
 * The full-screen inbox (RP-34): every notice, a page at a time, as New (unread) or All.
 * It refreshes every 20 seconds while open, so notices arrive as they come in, and each
 * new one can be opened or dismissed where it lands.
 */

import { BellOff } from "lucide-react";
import { NotificationItem } from "@/components/notifications/NotificationItem";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type InboxView, useNotificationInbox } from "@/hooks/use-notifications";

export function NotificationInbox({ view }: { view: InboxView }) {
  const { data, isLoading, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useNotificationInbox(view);
  const notices = data?.pages.flatMap((p) => p.data) ?? [];

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }
  if (isError) {
    return <ErrorState message="Could not load notifications" onRetry={() => refetch()} />;
  }
  if (notices.length === 0) {
    return view === "new" ? (
      <EmptyState
        icon={BellOff}
        title="You're all caught up"
        description="New notifications land here as they come in."
      />
    ) : (
      <EmptyState icon={BellOff} title="No notifications yet" />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="divide-y divide-border rounded-lg border border-border bg-card p-1.5">
        {notices.map((n) => (
          <NotificationItem key={n.id} notification={n} />
        ))}
      </div>
      {hasNextPage && (
        <Button
          variant="outline"
          className="self-center"
          disabled={isFetchingNextPage}
          onClick={() => fetchNextPage()}
        >
          {isFetchingNextPage ? "Loading…" : "Show older"}
        </Button>
      )}
    </div>
  );
}
